import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import StudentOnboarding from "@/components/student/StudentOnboarding";
import HomeworkHelper from "@/components/student/HomeworkHelper";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";
import { 
  Home, Volume2, VolumeX, Music, Music2, Send, 
  BookOpen, ClipboardCheck, HelpCircle, Check, MessageCircle,
  ArrowDown, ArrowRight, ArrowLeft, LogOut, Play, RefreshCw,
  SkipForward, Sparkles, GraduationCap, Lightbulb, AlertTriangle
} from "lucide-react";
import { LevelUpModal, AchievementBadge, Confetti, XPAnimation, AnimatedFunFact } from "@/components/ui/animations";
import MathRenderer, { FormattedMathText } from "@/components/student/learn/MathRenderer";
import { 
  playCorrect, 
  playIncorrect, 
  playLevelUp, 
  playXP, 
  playAchievement, 
  playSessionStart, 
  playSessionEnd,
  toggleSound,
  toggleMusic,
  stopMusic,
  hapticSuccess,
  hapticError,
  hapticLevelUp,
  hapticAchievement
} from "@/utils/soundEffects";

// Icon mapping for backend action icons
const ICON_MAP = {
  "book-open": BookOpen,
  "clipboard-check": ClipboardCheck,
  "help-circle": HelpCircle,
  "check": Check,
  "message-circle": MessageCircle,
  "arrow-down": ArrowDown,
  "arrow-right": ArrowRight,
  "arrow-left": ArrowLeft,
  "log-out": LogOut,
  "play": Play,
  "refresh-cw": RefreshCw,
  "skip-forward": SkipForward,
};

// Style mapping for backend action styles - vibrant colors
const STYLE_MAP = {
  primary: "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-lg hover:shadow-xl",
  secondary: "bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-200 hover:border-emerald-400 shadow-md hover:shadow-lg",
  ghost: "bg-transparent hover:bg-emerald-50 text-emerald-700 border border-emerald-200",
  destructive: "bg-gradient-to-r from-red-500 to-rose-500 hover:from-red-600 hover:to-rose-600 text-white shadow-lg",
  teach: "bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white shadow-lg hover:shadow-xl",
  practice: "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg hover:shadow-xl",
  question: "bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white shadow-lg hover:shadow-xl",
};

function StudentLearnContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionStartedRef = useRef(false);

  // User state
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Mode state - learning or homework helper (check URL param on init)
  const [mode, setMode] = useState(() => {
    const urlMode = searchParams.get("mode");
    return urlMode === "homework" ? "homework" : "learning";
  });

  // Session state - simplified to just track session ID
  const [sessionId, setSessionId] = useState(null);
  const [conversationState, setConversationState] = useState("idle");

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [inputConfig, setInputConfig] = useState({ enabled: true, placeholder: "Type your message..." });
  const [currentActions, setCurrentActions] = useState([]);
  const [sending, setSending] = useState(false);

  // Session stats (from backend metadata)
  const [sessionStats, setSessionStats] = useState({
    questionsAnswered: 0,
    correctAnswers: 0,
    startTime: null,
  });

  // Gamification state
  const [totalXP, setTotalXP] = useState(0);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [showAchievement, setShowAchievement] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showXPAnimation, setShowXPAnimation] = useState(false);
  
  // Sound state
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true); // Music enabled by default

  // Current topic/subject for header display
  const [currentTopic, setCurrentTopic] = useState(null);
  const [currentSubject, setCurrentSubject] = useState(null);
  const [showBackConfirm, setShowBackConfirm] = useState(false);

  // Handle browser back button - show confirmation to end session
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default navigation and show confirmation
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowBackConfirm(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch user profile
  const fetchProfile = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Initialize XP and level from user data - use actual xp_points from database
        const userXP = data.user.xp_points || 0;
        setTotalXP(userXP);
        setCurrentLevel(Math.floor(userXP / 100) + 1); // Level up every 100 XP
        
        // Check if this is a new student (no sessions completed)
        const totalSessions = data.user.total_sessions || 0;
        const onboardingCompleted = localStorage.getItem(`eduzy_onboarding_${data.user.id}`);
        if (totalSessions === 0 && !onboardingCompleted) {
          setShowOnboarding(true);
        }
      } else if (response.status === 401) {
        navigate("/");
      }
    } catch (error) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Auto-start session when page loads
  useEffect(() => {
    if (user && !loading && !sessionId && !sessionStartedRef.current && !showOnboarding) {
      sessionStartedRef.current = true;
      startSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, showOnboarding]);

  // Handle onboarding completion
  const handleOnboardingComplete = async (preferences) => {
    try {
      // Save preferences to backend
      await fetch(`${API}/auth/update-preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(preferences)
      });
      
      // Mark onboarding as complete locally
      localStorage.setItem(`eduzy_onboarding_${user.id}`, "true");
      setShowOnboarding(false);
      toast.success("Welcome! Let's start learning!");
    } catch (error) {
      console.error("Failed to save preferences:", error);
      // Continue anyway
      localStorage.setItem(`eduzy_onboarding_${user.id}`, "true");
      setShowOnboarding(false);
    }
  };

  const handleSkipOnboarding = () => {
    localStorage.setItem(`eduzy_onboarding_${user.id}`, "true");
    setShowOnboarding(false);
  };

  // Cleanup music on unmount
  useEffect(() => {
    return () => {
      stopMusic();
    };
  }, []);

  // Start background music by default when component mounts
  useEffect(() => {
    if (musicEnabled && user && !loading) {
      toggleMusic(true);
    }
  }, [user, loading, musicEnabled]);

  // Start a new conversation session
  const startSession = async () => {
    try {
      setSending(true);
      const response = await fetch(`${API}/conversation/session/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          student_id: user.id,
          platform: "web"
        })
      });

      if (!response.ok) throw new Error("Failed to start session");

      const data = await response.json();
      
      setSessionId(data.session_id);
      setConversationState(data.state);
      setSessionStats({ questionsAnswered: 0, correctAnswers: 0, startTime: new Date() });
      
      // Add greeting message
      addBotMessage(data.response.content, data.response.type, data.metadata);
      setCurrentActions(data.actions || []);
      setInputConfig(data.input || { enabled: true, placeholder: "Type your message..." });
      
      playSessionStart();
    } catch (error) {
      console.error("Session start error:", error);
      toast.error("Failed to start learning session");
    } finally {
      setSending(false);
    }
  };

  // Send message to backend
  const sendMessage = async (message, actionId = null) => {
    if (!sessionId) return;
    
    try {
      setSending(true);
      
      const response = await fetch(`${API}/conversation/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: sessionId,
          student_id: user.id,
          message: message,
          action: actionId,
          platform: "web"
        })
      });

      if (!response.ok) throw new Error("Failed to send message");

      const data = await response.json();
      
      // Update state from backend
      setConversationState(data.state);
      
      // Add bot response
      addBotMessage(data.response.content, data.response.type, data.metadata);
      setCurrentActions(data.actions || []);
      setInputConfig(data.input || { enabled: true, placeholder: "Type your message..." });
      
      // Update topic/subject from metadata
      if (data.metadata?.topic) setCurrentTopic(data.metadata.topic);
      if (data.metadata?.subject) setCurrentSubject(data.metadata.subject);
      
      // Handle gamification for feedback responses
      if (data.response.type === "feedback") {
        handleFeedbackResponse(data.response.content);
      }
      
      // Handle session end
      if (data.state === "session_end") {
        handleSessionEnd(data);
      }
      
    } catch (error) {
      console.error("Message error:", error);
      toast.error("Failed to send message");
      addBotMessage("Sorry, something went wrong. Please try again.", "error");
    } finally {
      setSending(false);
    }
  };

  // Handle feedback response (for gamification)
  const handleFeedbackResponse = (content) => {
    const isCorrect = content.includes("✅") || content.toLowerCase().includes("correct");
    
    if (isCorrect) {
      // Play sounds and haptics
      playCorrect();
      hapticSuccess();
      
      // Show confetti and XP animation
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
      setShowXPAnimation(true);
      setTimeout(() => {
        setShowXPAnimation(false);
        playXP();
      }, 300);
      
      // Update stats and XP
      const newQuestionsAnswered = sessionStats.questionsAnswered + 1;
      const newCorrectAnswers = sessionStats.correctAnswers + 1;
      const newXP = totalXP + 10;
      const newLevel = Math.floor(newXP / 100) + 1; // Level up every 100 XP
      
      setSessionStats(prev => ({
        ...prev,
        questionsAnswered: newQuestionsAnswered,
        correctAnswers: newCorrectAnswers
      }));
      setTotalXP(newXP);
      
      // Check for level up
      if (newLevel > currentLevel) {
        setCurrentLevel(newLevel);
        setTimeout(() => {
          setShowLevelUp(true);
          playLevelUp();
          hapticLevelUp();
        }, 500);
      }
      
      // Check for achievements
      if (newQuestionsAnswered === 1) {
        triggerAchievement('first_question');
      } else if (newCorrectAnswers === 5 && newQuestionsAnswered === 5) {
        triggerAchievement('perfect_5');
      }
    } else {
      // Wrong answer
      playIncorrect();
      hapticError();
      setSessionStats(prev => ({
        ...prev,
        questionsAnswered: prev.questionsAnswered + 1
      }));
    }
  };

  const triggerAchievement = (badge) => {
    setCurrentAchievement(badge);
    setTimeout(() => {
      setShowAchievement(true);
      playAchievement();
      hapticAchievement();
    }, 1000);
  };

  const handleSessionEnd = (data) => {
    const duration = sessionStats.startTime ? Math.round((new Date() - sessionStats.startTime) / 60000) : 0;
    const accuracy = sessionStats.questionsAnswered > 0 
      ? Math.round((sessionStats.correctAnswers / sessionStats.questionsAnswered) * 100) 
      : 0;

    toast.success(`Session complete! ${sessionStats.questionsAnswered} questions, ${accuracy}% accuracy, ${duration} min`);
    playSessionEnd();
  };

  // Add message to chat
  const addBotMessage = (content, type, metadata = {}) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: "bot",
      content,
      responseType: type,
      metadata
    }]);
  };

  const addUserMessage = (content) => {
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: "user",
      content
    }]);
  };

  // Handle action button click
  const handleActionClick = (action) => {
    addUserMessage(action.label);
    sendMessage(action.label, action.id);
  };

  // Handle text input submit
  const handleSendMessage = () => {
    if (!inputValue.trim() || sending) return;
    
    const message = inputValue.trim();
    setInputValue("");
    addUserMessage(message);
    sendMessage(message);
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle sound
  const handleToggleSound = () => {
    const newState = toggleSound();
    setSoundEnabled(newState);
    toast.success(newState ? "Sound effects enabled" : "Sound effects muted");
  };

  // Toggle music
  const handleToggleMusic = () => {
    const newState = toggleMusic();
    setMusicEnabled(newState);
    toast.success(newState ? "Background music on" : "Background music off");
  };

  // End session and go back
  const handleEndSession = () => {
    if (sessionId) {
      sendMessage("done", "done");
    }
    setTimeout(() => navigate("/student/profile"), 500);
  };

  // Render action button with icon - vibrant styling
  const renderActionButton = (action, index) => {
    const IconComponent = ICON_MAP[action.icon] || null;
    
    // Map action IDs to specific vibrant styles
    const getActionStyle = (actionId, defaultStyle) => {
      if (actionId?.includes('teach') || actionId?.includes('learn')) return STYLE_MAP.teach;
      if (actionId?.includes('practice') || actionId?.includes('quiz')) return STYLE_MAP.practice;
      if (actionId?.includes('question') || actionId?.includes('ask')) return STYLE_MAP.question;
      return STYLE_MAP[defaultStyle] || STYLE_MAP.secondary;
    };
    
    const styleClass = getActionStyle(action.id, action.style);
    
    return (
      <Button
        key={action.id || index}
        onClick={() => handleActionClick(action)}
        disabled={sending}
        className={`${styleClass} flex items-center gap-2 px-5 py-2.5 rounded-xl transition-all duration-300 hover:scale-105 hover:-translate-y-0.5 font-medium action-btn-glow`}
        data-testid={`action-${action.id}`}
      >
        {IconComponent && <IconComponent className="w-4 h-4" />}
        {action.label}
      </Button>
    );
  };

  // Parse fun fact from lesson content
  const parseFunFact = (content) => {
    const funFactMatch = content.match(/\n?\n?(?:Did You Know\?|Fun Fact:?|DID YOU KNOW\?|FUN FACT:?)\s*(.*?)$/is);
    if (funFactMatch) {
      const mainContent = content.replace(/\n?\n?(?:Did You Know\?|Fun Fact:?|DID YOU KNOW\?|FUN FACT:?)\s*.*$/is, '').trim();
      return { mainContent, funFact: funFactMatch[1].trim() };
    }
    return { mainContent: content, funFact: null };
  };

  // Render message bubble with vibrant styling
  const renderMessage = (msg) => {
    const isBot = msg.type === "bot";
    const responseType = msg.responseType;
    
    // Determine message styling based on response type
    const isLesson = responseType === "lesson";
    const isQuestion = responseType === "question";
    const isFeedback = responseType === "feedback";
    const isCorrect = isFeedback && (msg.content.includes("✅") || msg.content.toLowerCase().includes("correct") || msg.content.toLowerCase().includes("excellent") || msg.content.toLowerCase().includes("great job"));
    const isIncorrect = isFeedback && (msg.content.includes("❌") || msg.content.toLowerCase().includes("not quite") || msg.content.toLowerCase().includes("incorrect"));
    const isGreeting = responseType === "greeting";
    
    // Parse fun fact for lessons
    const { mainContent, funFact } = isLesson ? parseFunFact(msg.content) : { mainContent: msg.content, funFact: null };
    
    // User message styling
    if (!isBot) {
      return (
        <div key={msg.id} className="flex justify-end mb-4 animate-message-right" data-testid={`message-${msg.id}`}>
          <div className="max-w-[85%]">
            <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-lg transform transition-all duration-200 hover:scale-[1.02]">
              <p className="leading-relaxed">{msg.content}</p>
            </div>
          </div>
        </div>
      );
    }

    // Bot message with vibrant styling
    return (
      <div key={msg.id} className="flex justify-start mb-4 animate-message-left" data-testid={`message-${msg.id}`}>
        <div className="max-w-[90%] md:max-w-[85%]">
          <div className="flex items-start gap-2">
            {/* Animated Avatar */}
            <div className={`w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg ${sending ? 'animate-pulse' : ''}`}>
              <GraduationCap className="w-5 h-5 text-white" />
            </div>
            
            <div className="space-y-2 flex-1 min-w-0">
              {/* Message Container with dynamic styling */}
              <div className={`rounded-2xl rounded-tl-sm p-4 transition-all duration-300 transform hover:scale-[1.01] ${
                isLesson ? "bg-white border-2 border-emerald-200 shadow-md animate-lesson-reveal" :
                isQuestion ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 shadow-lg animate-question-bounce" :
                isCorrect ? "bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 shadow-lg animate-correct-pulse" :
                isIncorrect ? "bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-300 shadow-md" :
                isGreeting ? "bg-gradient-to-br from-purple-50 to-violet-50 border-2 border-purple-200 shadow-md" :
                "bg-white border border-gray-200 shadow-sm"
              }`}>
                {/* Lesson Header */}
                {isLesson && msg.metadata?.topic && (
                  <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold mb-3 pb-2 border-b border-emerald-100">
                    <BookOpen className="h-4 w-4" />
                    <span>{msg.metadata.subject || 'Learning'}</span>
                    <ArrowRight className="h-3 w-3 text-gray-400" />
                    <span className="text-emerald-600">{msg.metadata.topic}</span>
                  </div>
                )}
                
                {/* Question Header with animation */}
                {isQuestion && (
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-semibold mb-3 pb-2 border-b border-blue-200">
                    <HelpCircle className="h-4 w-4 animate-pulse" />
                    <span>Question Time!</span>
                    <Sparkles className="h-4 w-4 ml-auto text-yellow-500 animate-bounce" />
                  </div>
                )}

                {/* Correct Answer Header */}
                {isCorrect && (
                  <div className="flex items-center gap-2 text-green-700 text-sm font-bold mb-3 pb-2 border-b border-green-200">
                    <span className="text-2xl animate-bounce">🎉</span>
                    <span>Awesome!</span>
                    <span className="ml-auto bg-gradient-to-r from-green-400 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                      +10 XP
                    </span>
                  </div>
                )}

                {/* Wrong Answer Header */}
                {isIncorrect && (
                  <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-3 pb-2 border-b border-red-200">
                    <span className="text-xl">💪</span>
                    <span>Keep trying! You&apos;ve got this!</span>
                  </div>
                )}
                
                {/* Main Content */}
                <div className="prose prose-sm max-w-none text-gray-800">
                  <FormattedMathText text={mainContent} />
                </div>
                
                {/* Animated Fun Fact Card for Lessons */}
                {funFact && (
                  <div className="mt-4">
                    <AnimatedFunFact 
                      fact={funFact} 
                      renderMath={(text) => <FormattedMathText text={text} />} 
                    />
                  </div>
                )}
              </div>
              
              {/* Response type badge - Only show for questions and feedback, NOT lessons */}
              {isBot && responseType && ["question", "feedback"].includes(responseType) && (
                <div className="flex items-center gap-2 ml-1">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium shadow-sm ${
                    isQuestion ? "bg-blue-100 text-blue-700 border border-blue-200" :
                    isCorrect ? "bg-green-100 text-green-700 border border-green-200" :
                    isIncorrect ? "bg-red-100 text-red-700 border border-red-200" :
                    "bg-gray-100 text-gray-600"
                  }`}>
                    {isQuestion ? "❓ Question" : 
                     isCorrect ? "✅ Correct" :
                     isIncorrect ? "🔄 Try Again" :
                     "Feedback"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show onboarding for new students
  if (showOnboarding && user) {
    return (
      <StudentOnboarding
        studentName={user.name?.split(' ')[0] || 'Student'}
        studentGrade={user.grade || 5}
        onComplete={handleOnboardingComplete}
        onSkip={handleSkipOnboarding}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Gamification Modals */}
      <LevelUpModal 
        level={currentLevel} 
        show={showLevelUp} 
        onClose={() => setShowLevelUp(false)} 
      />
      <AchievementBadge 
        badge={currentAchievement} 
        show={showAchievement} 
        onComplete={() => setShowAchievement(false)} 
      />
      {showConfetti && <Confetti />}
      {showXPAnimation && <XPAnimation points={10} />}

      {/* Homework Helper Mode */}
      {mode === "homework" ? (
        <HomeworkHelper
          childId={user?.student_id || user?.id}
          studentName={user?.name}
          grade={user?.grade}
          onBack={() => setMode("learning")}
        />
      ) : (
        <>
          {/* Unified Header with Sound/Music Controls */}
          <UnifiedPortalHeader
            portalType="student"
            userName=""
            userSubtitle=""
            onLogout={handleEndSession}
            showUserInfo={false}
            showHomeButton={false}
            largeLogout={false}
            hideLogout={true}
            rightContent={
              <div className="flex items-center gap-2">
                {/* Topic display */}
                {currentTopic && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full">
                    <span className="text-xs text-emerald-600">{currentSubject}:</span>
                    <span className="font-medium text-emerald-700 text-sm">{currentTopic}</span>
                  </div>
                )}
                
                {/* Session Score */}
                {sessionStats.questionsAnswered > 0 && (
                  <div className="hidden sm:flex items-center gap-1.5 bg-emerald-100 px-3 py-1.5 rounded-full">
                    <span className="text-xs text-emerald-600">Score:</span>
                    <span className="font-bold text-emerald-700 text-sm">
                      {sessionStats.correctAnswers}/{sessionStats.questionsAnswered}
                    </span>
                  </div>
                )}
                
                {/* Sound/Music toggles */}
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleToggleSound}
                    className="rounded-full w-9 h-9 bg-gray-100 hover:bg-gray-200"
                    data-testid="toggle-sound"
                  >
                    {soundEnabled ? <Volume2 className="w-4 h-4 text-emerald-600" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleToggleMusic}
                    className="rounded-full w-9 h-9 bg-gray-100 hover:bg-gray-200"
                    data-testid="toggle-music"
                  >
                    {musicEnabled ? <Music className="w-4 h-4 text-emerald-600" /> : <Music2 className="w-4 h-4 text-gray-400" />}
                  </Button>
                </div>
                
                {/* End Learning Session Button */}
                <Button
                  size="sm"
                  onClick={handleEndSession}
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold px-4"
                  data-testid="end-session"
                >
                  <LogOut className="w-4 h-4 mr-1.5" />
                  <span className="hidden sm:inline">End Learning Session</span>
                  <span className="sm:hidden">End</span>
                </Button>
              </div>
            }
          />

      {/* Main chat area */}
      <main className="flex-1 flex flex-col max-w-4xl mx-auto w-full">
        <div className="flex-1 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
            {messages.map(renderMessage)}
            
            {/* Enhanced Typing indicator */}
            {sending && (
              <div className="flex justify-start mb-4">
                <div className="flex items-start gap-2">
                  <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg animate-pulse">
                    <GraduationCap className="w-5 h-5 text-white" />
                  </div>
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl rounded-tl-sm px-5 py-3 border border-gray-200 shadow-md">
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600 text-sm font-medium">Eduzy is thinking</span>
                      <div className="flex gap-1.5">
                        <span className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full animate-bounce shadow-sm" style={{ animationDelay: "0ms" }} />
                        <span className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full animate-bounce shadow-sm" style={{ animationDelay: "150ms" }} />
                        <span className="w-2.5 h-2.5 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full animate-bounce shadow-sm" style={{ animationDelay: "300ms" }} />
                      </div>
                      <Sparkles className="w-4 h-4 text-yellow-500 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Action buttons */}
          {currentActions.length > 0 && !sending && (
            <div className="px-4 pb-2">
              <div className="flex flex-wrap gap-2 justify-center" data-testid="action-buttons">
                {currentActions.map(renderActionButton)}
              </div>
            </div>
          )}

          {/* Vibrant Input area - extra bottom padding for mobile to avoid Emergent banner overlap */}
          <div className="p-4 pb-20 sm:pb-4 bg-gradient-to-r from-white/90 to-emerald-50/90 backdrop-blur-sm border-t-2 border-emerald-100">
            <div className="max-w-3xl mx-auto flex gap-3">
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={inputConfig.placeholder}
                disabled={!inputConfig.enabled || sending}
                className="flex-1 rounded-full border-2 border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 shadow-sm px-5 py-2.5 transition-all duration-200 hover:border-emerald-300 chat-input-glow"
                data-testid="chat-input"
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || sending || !inputConfig.enabled}
                className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                data-testid="send-button"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </main>
      
      {/* Back Button Confirmation Dialog */}
      <Dialog open={showBackConfirm} onOpenChange={setShowBackConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
            <DialogTitle className="text-center">End Learning Session?</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to leave? Your current session progress will be saved, but you&apos;ll need to start a new session to continue learning.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              onClick={() => setShowBackConfirm(false)}
              className="sm:w-auto"
            >
              Continue Learning
            </Button>
            <Button
              onClick={handleEndSession}
              className="bg-red-500 hover:bg-red-600 text-white sm:w-auto"
            >
              <LogOut className="w-4 h-4 mr-2" />
              End Session & Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Unified Footer */}
      <SharedFooter />
        </>
      )}
    </div>
  );
}

// Export with ErrorBoundary wrapper
export default function StudentLearn() {
  return (
    <ErrorBoundary>
      <StudentLearnContent />
    </ErrorBoundary>
  );
}
